import React, { useMemo, useState } from 'react';
import { RmmReconciliationCustomer, RmmReconciliationDevice, RmmReconciliationResult } from '../../services/rmmService';
import { formatApiInstant } from '../../services/rmmSyncSchedule';

type StatusTab = 'ninjaStale' | 'notInstalled' | 'installedNotSignedIn' | 'agentQuiet' | 'installUnknown' | 'healthy';

interface StatusTabDef {
  id: StatusTab;
  label: string;
  hint: string;
  count: number;
  rows: RmmReconciliationDevice[];
  problem: boolean;
  countClass: string;
  activeClass: string;
}

export function hasDeviceStatus(c: RmmReconciliationCustomer): boolean {
  return typeof c.ninjaStaleCount === 'number';
}

function softwareLabel(d: RmmReconciliationDevice): { text: string; className: string } {
  if (!d.softwareChecked) return { text: 'Waiting for sync', className: 'text-slate-500' };
  const name = (d.aitryxProductName || '').trim();
  const version = (d.aitryxProductVersion || '').trim();
  if (!name && !version) return { text: 'Not installed', className: 'text-amber-700' };
  const exact = name.toLowerCase() === 'aitryx';
  const text = !exact && name ? (version ? `${name} ${version}` : name) : (version || name);
  return { text, className: 'text-emerald-700' };
}

function tabsFor(c: RmmReconciliationCustomer): StatusTabDef[] {
  const tabs: StatusTabDef[] = [
    {
      id: 'ninjaStale',
      label: 'Ninja stale',
      hint: 'No Ninja contact for 30+ days',
      count: c.ninjaStaleCount ?? 0,
      rows: c.ninjaStale ?? [],
      problem: true,
      countClass: 'text-rose-700',
      activeClass: 'border-rose-300 bg-rose-50',
    },
    {
      id: 'notInstalled',
      label: 'Aitryx not installed',
      hint: 'Ninja software inventory has no Aitryx product',
      count: c.notInstalledCount ?? 0,
      rows: c.notInstalled ?? [],
      problem: true,
      countClass: 'text-amber-700',
      activeClass: 'border-amber-300 bg-amber-50',
    },
    {
      id: 'installedNotSignedIn',
      label: 'Aitryx not signed in',
      hint: 'Aitryx is installed. Nobody has signed into Aitryx on this PC',
      count: c.installedNotSignedInCount ?? 0,
      rows: c.installedNotSignedIn ?? [],
      problem: true,
      countClass: 'text-amber-700',
      activeClass: 'border-amber-300 bg-amber-50',
    },
    {
      id: 'agentQuiet',
      label: 'Aitryx offline',
      hint: 'Aitryx was signed in, then sent nothing for more than 7 days. Ninja contact can still be recent',
      count: c.agentQuietCount ?? 0,
      rows: c.agentQuiet ?? [],
      problem: true,
      countClass: 'text-rose-700',
      activeClass: 'border-rose-300 bg-rose-50',
    },
    {
      id: 'installUnknown',
      label: 'Waiting for sync',
      hint: 'Software inventory has not been read',
      count: c.installUnknownCount ?? 0,
      rows: c.installUnknown ?? [],
      problem: true,
      countClass: 'text-slate-700',
      activeClass: 'border-slate-300 bg-slate-50',
    },
    {
      id: 'healthy',
      label: 'Aitryx online',
      hint: 'Aitryx uploaded telemetry in the last 7 days',
      count: c.healthyCount ?? 0,
      rows: c.healthy ?? [],
      problem: false,
      countClass: 'text-emerald-700',
      activeClass: 'border-emerald-300 bg-emerald-50',
    },
  ];
  return tabs.filter((tab) => tab.id !== 'installUnknown' || tab.count > 0);
}

function firstTab(tabs: StatusTabDef[]): StatusTab {
  return tabs.find((tab) => tab.problem && tab.count > 0)?.id ?? 'healthy';
}

type SortKey = 'hostName' | 'serialNumber' | 'model' | 'lastLoggedOnUser' | 'lastContactUtc' | 'installed' | 'aitryxLastSeenUtc' | 'aitryxUser';

const COLUMNS: Array<{ key: SortKey; label: string; width: string }> = [
  { key: 'hostName', label: 'Hostname', width: '13%' },
  { key: 'serialNumber', label: 'Serial', width: '9%' },
  { key: 'model', label: 'Model', width: '16%' },
  { key: 'lastLoggedOnUser', label: 'Windows login', width: '14%' },
  { key: 'lastContactUtc', label: 'Ninja last contact', width: '12%' },
  { key: 'installed', label: 'Aitryx installed', width: '9%' },
  { key: 'aitryxLastSeenUtc', label: 'Aitryx last seen', width: '12%' },
  { key: 'aitryxUser', label: 'Aitryx user', width: '15%' },
];

function timeValue(iso?: string | null): number | null {
  if (!iso) return null;
  const value = Date.parse(iso);
  return Number.isNaN(value) ? null : value;
}

function sortText(device: RmmReconciliationDevice, key: SortKey): string {
  switch (key) {
    case 'hostName': return device.hostName || '';
    case 'serialNumber': return device.serialNumber || '';
    case 'model': return device.model || '';
    case 'lastLoggedOnUser': return device.lastLoggedOnUser || '';
    case 'installed': return softwareLabel(device).text;
    case 'aitryxUser': return [device.aitryxUserName, device.aitryxUserEmail].filter(Boolean).join(' ');
    default: return '';
  }
}

function compareDevices(a: RmmReconciliationDevice, b: RmmReconciliationDevice, key: SortKey, dir: 'asc' | 'desc'): number {
  const sign = dir === 'asc' ? 1 : -1;
  if (key === 'lastContactUtc' || key === 'aitryxLastSeenUtc') {
    const aTime = timeValue(deviceTime(a, key));
    const bTime = timeValue(deviceTime(b, key));
    if (aTime == null && bTime == null) return 0;
    if (aTime == null) return 1;
    if (bTime == null) return -1;
    return (aTime - bTime) * sign;
  }
  return sortText(a, key).localeCompare(sortText(b, key), undefined, { numeric: true, sensitivity: 'base' }) * sign;
}

function deviceTime(device: RmmReconciliationDevice, key: 'lastContactUtc' | 'aitryxLastSeenUtc'): string | null | undefined {
  return key === 'lastContactUtc' ? device.lastContactUtc : device.aitryxLastSeenUtc;
}

export function RmmFleetStatusTiles({
  ninja,
  ninjaStale,
  notInstalled,
  installedNotSignedIn,
  agentQuiet,
  healthy,
  installUnknown,
}: {
  ninja: number;
  ninjaStale: number;
  notInstalled: number;
  installedNotSignedIn: number;
  agentQuiet: number;
  healthy: number;
  installUnknown: number;
}) {
  const tiles = [
    { label: 'Ninja devices', value: ninja, className: 'text-slate-900' },
    { label: 'Ninja stale', value: ninjaStale, className: ninjaStale > 0 ? 'text-rose-700' : 'text-slate-900' },
    { label: 'Aitryx not installed', value: notInstalled, className: notInstalled > 0 ? 'text-amber-700' : 'text-slate-900' },
    { label: 'Aitryx not signed in', value: installedNotSignedIn, className: installedNotSignedIn > 0 ? 'text-amber-700' : 'text-slate-900' },
    { label: 'Aitryx offline', value: agentQuiet, className: agentQuiet > 0 ? 'text-rose-700' : 'text-slate-900' },
    { label: 'Aitryx online', value: healthy, className: 'text-emerald-700' },
  ];
  if (installUnknown > 0) {
    tiles.push({ label: 'Waiting for sync', value: installUnknown, className: 'text-slate-700' });
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
      {tiles.map((tile) => (
        <div key={tile.label} className="rounded-lg border border-slate-200 p-3">
          <div className={`text-2xl font-semibold ${tile.className}`}>{tile.value}</div>
          <div className="text-xs text-slate-500">{tile.label}</div>
        </div>
      ))}
    </div>
  );
}

export function RmmDeviceStatusPanel({ c: customer }: { c: RmmReconciliationCustomer }) {
  const tabs = tabsFor(customer);
  const [chosen, setChosen] = useState<StatusTab | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'lastContactUtc', dir: 'desc' });
  const activeId = chosen && tabs.some((tab) => tab.id === chosen) ? chosen : firstTab(tabs);
  const active = tabs.find((tab) => tab.id === activeId) ?? tabs[tabs.length - 1];
  const rows = useMemo(
    () => [...active.rows].sort((a, b) => compareDevices(a, b, sort.key, sort.dir)),
    [active.rows, sort],
  );

  const chooseTab = (id: StatusTab) => {
    setChosen(id);
    setSort({ key: 'lastContactUtc', dir: 'desc' });
  };

  const chooseSort = (key: SortKey) => {
    setSort((current) => {
      if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
      const dates = key === 'lastContactUtc' || key === 'aitryxLastSeenUtc';
      return { key, dir: dates ? 'desc' : 'asc' };
    });
  };

  if (!hasDeviceStatus(customer)) {
    return (
      <p className="text-sm text-slate-600">
        This report is still the older missing-agent view. It will show Ninja stale, install, and sign-in after Core is updated.
      </p>
    );
  }

  return (
    <div className="w-full min-w-0 space-y-3">
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Device status">
        {tabs.map((tab) => {
          const selected = tab.id === active.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => chooseTab(tab.id)}
              className={`rounded-lg border px-3 py-2 text-left ${selected ? tab.activeClass : 'border-slate-200 bg-white hover:bg-slate-50'}`}
            >
              <div className={`text-lg font-semibold leading-none ${tab.countClass}`}>{tab.count}</div>
              <div className="mt-1 text-xs font-medium text-slate-700">{tab.label}</div>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-slate-500">
        {customer.ninjaCount} Ninja devices. {active.hint}. A device is signed in only after someone signs into Aitryx and telemetry is uploaded. Ninja&apos;s software list is a separate fact.
      </p>

      {active.count === 0 ? (
        <p className="text-sm text-slate-500">None in this list.</p>
      ) : (
        <div className="w-full min-w-0 overflow-hidden rounded-md border border-slate-300">
          <table className="w-full table-fixed border-collapse text-xs leading-4">
            <thead>
              <tr className="border-b border-slate-300 bg-slate-100 text-left text-slate-700">
                {COLUMNS.map((column) => {
                  const selected = sort.key === column.key;
                  return (
                    <th key={column.key} style={{ width: column.width }} className="px-2 py-1 font-semibold" aria-sort={selected ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      <button type="button" onClick={() => chooseSort(column.key)} className="flex w-full min-w-0 items-center gap-1 text-left">
                        <span className="truncate">{column.label}</span>
                        <span className={`shrink-0 ${selected ? 'text-slate-900' : 'text-slate-400'}`}>{selected ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((device, index) => {
                const software = softwareLabel(device);
                const user = [device.aitryxUserName, device.aitryxUserEmail].filter(Boolean).join(' · ');
                const contact = formatApiInstant(device.lastContactUtc) || 'No contact time';
                const seen = formatApiInstant(device.aitryxLastSeenUtc) || 'Never';
                const cells = [
                  device.hostName || '—',
                  device.serialNumber || '—',
                  device.model || '—',
                  device.lastLoggedOnUser || '—',
                  contact,
                  software.text,
                  seen,
                  user || '—',
                ];
                return (
                  <tr key={`${device.serialNumber || ''}-${device.hostName || ''}-${index}`} className="border-b border-slate-200 last:border-b-0 odd:bg-white even:bg-slate-50">
                    {cells.map((value, cellIndex) => (
                      <td
                        key={COLUMNS[cellIndex].key}
                        title={value}
                        className={`max-w-0 truncate px-2 py-1 ${cellIndex === 1 ? 'font-mono text-slate-800' : 'text-slate-900'} ${cellIndex === 5 ? `font-medium ${software.className}` : ''}`}
                      >
                        {value}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {active.count > active.rows.length && (
        <p className="text-xs text-slate-500">
          Showing {active.rows.length} of {active.count}. Export CSV from the customer page for the full list.
        </p>
      )}

      {(customer.aitryxOnlyCount ?? 0) > 0 && (
        <div className="w-full min-w-0 space-y-1">
          <p className="text-xs text-slate-600">
            {customer.aitryxOnlyCount} Aitryx {customer.aitryxOnlyCount === 1 ? 'device has' : 'devices have'} no matching serial or hostname in this Ninja organization. They are not part of the {customer.ninjaCount} Ninja devices.
          </p>
          {(customer.aitryxOnly ?? []).length > 0 && (
            <div className="w-full min-w-0 overflow-hidden rounded-md border border-slate-300">
              <table className="w-full table-fixed border-collapse text-xs leading-4">
                <thead>
                  <tr className="border-b border-slate-300 bg-slate-100 text-left text-slate-700">
                    <th className="w-[22%] px-2 py-1 font-semibold">Hostname</th>
                    <th className="w-[14%] px-2 py-1 font-semibold">Serial</th>
                    <th className="w-[22%] px-2 py-1 font-semibold">Model</th>
                    <th className="w-[14%] px-2 py-1 font-semibold">Aitryx last seen</th>
                    <th className="w-[28%] px-2 py-1 font-semibold">Aitryx user</th>
                  </tr>
                </thead>
                <tbody>
                  {(customer.aitryxOnly ?? []).map((device, index) => {
                    const user = [device.aitryxUserName, device.aitryxUserEmail].filter(Boolean).join(' · ');
                    const cells = [
                      device.hostName || '—',
                      device.serialNumber || '—',
                      device.model || '—',
                      formatApiInstant(device.aitryxLastSeenUtc) || 'Never',
                      user || '—',
                    ];
                    return (
                      <tr key={`${device.serialNumber || ''}-${device.hostName || ''}-${index}`} className="border-b border-slate-200 last:border-b-0 odd:bg-white even:bg-slate-50">
                        {cells.map((value, cellIndex) => (
                          <td key={cellIndex} title={value} className="max-w-0 truncate px-2 py-1 text-slate-900">{value}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function RmmFleetStatus({ recon }: { recon: RmmReconciliationResult }) {
  if (typeof recon.totalNinjaStale !== 'number') {
    return (
      <p className="text-sm text-slate-600">
        Install and sign-in status needs the updated Core service. Sync after that deploy before treating a device as not installed.
      </p>
    );
  }
  return (
    <RmmFleetStatusTiles
      ninja={recon.totalNinja}
      ninjaStale={recon.totalNinjaStale}
      notInstalled={recon.totalNotInstalled ?? 0}
      installedNotSignedIn={recon.totalInstalledNotSignedIn ?? 0}
      agentQuiet={recon.totalAgentQuiet ?? 0}
      healthy={recon.totalHealthy ?? 0}
      installUnknown={recon.totalInstallUnknown ?? 0}
    />
  );
}

export function deviceProblemBadges(c: RmmReconciliationCustomer): Array<{ label: string; variant: 'destructive' | 'warning' | 'secondary' | 'info' }> {
  if (!hasDeviceStatus(c)) return [];
  const badges: Array<{ label: string; variant: 'destructive' | 'warning' | 'secondary' | 'info' }> = [];
  if ((c.ninjaStaleCount ?? 0) > 0) badges.push({ label: `${c.ninjaStaleCount} Ninja stale`, variant: 'destructive' });
  if ((c.notInstalledCount ?? 0) > 0) badges.push({ label: `${c.notInstalledCount} Aitryx not installed`, variant: 'warning' });
  if ((c.installedNotSignedInCount ?? 0) > 0) badges.push({ label: `${c.installedNotSignedInCount} Aitryx not signed in`, variant: 'warning' });
  if ((c.agentQuietCount ?? 0) > 0) badges.push({ label: `${c.agentQuietCount} Aitryx offline`, variant: 'info' });
  if ((c.installUnknownCount ?? 0) > 0) badges.push({ label: `${c.installUnknownCount} waiting for sync`, variant: 'secondary' });
  return badges;
}
